import {
  estadoInicialDaNavegacao,
  lerHistorico,
  navegarParaAnterior,
  navegarParaProximo,
  registrarPrompt,
} from '@modules/aawz-intelligence-v2/utils/historico-de-prompts'
import { falaDoAtalho } from '@modules/aawz-intelligence-v2/utils/comando-na-fala'

const QUEBRA_DE_LINHA = '\n'

// Os refs do composer que abrem painel — todos expõem `isOpen()`. O menu do
// "/" (comandos + Atalhos) é o mesmo `ferramentas`. O `VozSelect` fica de fora:
// ele só existe com `sessaoDeVozAtiva`, que já bloqueia a navegação.
const REFS_DE_MENU = ['ferramentas', 'agente', 'modelo', 'escopo']

const temModificador = (evento) =>
  evento.shiftKey || evento.altKey || evento.ctrlKey || evento.metaKey

/**
 * ↑/↓ percorrem os últimos prompts enviados (`utils/historico-de-prompts.js`).
 *
 * O host (`send-message.vue`) só chama `tratarSetaDoHistorico` no `onKeydown`.
 * Envio e digitação são ouvidos na própria instância (`$on`), para o diff do
 * composer ficar mínimo. Lê do host: `value`, `gravando`, `sessaoDeVozAtiva`,
 * `loading`, `$refs`, `elementoDoCampo`, `medirAltura`.
 */
export const sendMessageHistoricoMixin = {
  props: {
    // Ligado em todas as superfícies: a chave do storage já separa por usuário.
    historicoDePrompts: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { navegacaoDoHistorico: estadoInicialDaNavegacao() }
  },
  created() {
    // `this.value` e não o texto do `send`: envio só com anexo leva uma frase
    // gerada no lugar do texto, e ela não é prompt do usuário. O prefixo do
    // módulo entra: o histórico guarda a fala como foi enviada.
    this.$on('send', () => this.registrarNoHistorico(this.falaDoEnvioParaHistorico()))
    this.$on('executar-atalho', (atalho) =>
      this.registrarNoHistorico(falaDoAtalho(atalho.label, atalho.argumento))
    )
    this.$on('input', () => {
      if (!this._aplicandoHistorico) this.reiniciarNavegacaoDoHistorico()
    })
  },
  computed: {
    escopoDoHistorico() {
      const user = (this.$currentUser && this.$currentUser()) || {}
      const escritorio = user.escritorio || {}
      return { idEscritorio: escritorio.id_interno, idProfissional: user.id_interno }
    },
  },
  methods: {
    /** O ponto único de "há painel do composer aberto" — com ele, as setas são do menu. */
    algumMenuAberto() {
      return REFS_DE_MENU.some((nome) => {
        const menu = this.$refs[nome]
        return Boolean(menu && typeof menu.isOpen === 'function' && menu.isOpen())
      })
    },
    /** `true` quando a tecla foi consumida pelo histórico. */
    tratarSetaDoHistorico(evento) {
      if (!this.podeNavegarNoHistorico(evento)) return false
      const lista = lerHistorico({ escopo: this.escopoDoHistorico })
      const passo =
        evento.key === 'ArrowUp'
          ? navegarParaAnterior(lista, this.navegacaoDoHistorico, this.value)
          : navegarParaProximo(lista, this.navegacaoDoHistorico)
      if (!passo) return false
      evento.preventDefault()
      this.navegacaoDoHistorico = passo.estado
      this.aplicarTextoDoHistorico(passo.texto)
      return true
    },
    podeNavegarNoHistorico(evento) {
      const seta = evento.key === 'ArrowUp' || evento.key === 'ArrowDown'
      if (!seta || !this.historicoDePrompts || temModificador(evento)) return false
      if (evento.isComposing || this.gravando || this.sessaoDeVozAtiva) return false
      if (this.loading) return false
      if (this.algumMenuAberto()) return false
      const campo = evento.target
      if (!campo || campo.tagName !== 'TEXTAREA') return false
      return evento.key === 'ArrowUp'
        ? this.cursorNaPrimeiraLinha(campo)
        : this.cursorNaUltimaLinha(campo)
    },
    cursorNaPrimeiraLinha(campo) {
      const texto = String(campo.value || '')
      return !texto.slice(0, campo.selectionStart).includes(QUEBRA_DE_LINHA)
    },
    cursorNaUltimaLinha(campo) {
      const texto = String(campo.value || '')
      return !texto.slice(campo.selectionEnd).includes(QUEBRA_DE_LINHA)
    },
    // Sem `reagirAoComando`: um prompt antigo que comece com `/` não pode abrir
    // o menu — a próxima seta passaria a navegar nele, e não no histórico.
    aplicarTextoDoHistorico(texto) {
      const caixa = this.$refs.caixa
      this.alturaDaCaixaAntes = caixa ? caixa.offsetHeight : 0
      this._aplicandoHistorico = true
      this.$emit('input', texto)
      this._aplicandoHistorico = false
      this.medirAltura()
      this.$nextTick(() => {
        const campo = this.elementoDoCampo()
        if (!campo) return
        const fim = String(campo.value || '').length
        campo.setSelectionRange(fim, fim)
      })
    },
    // Digitar solta a navegação: o texto editado vira o rascunho da próxima ↑.
    reiniciarNavegacaoDoHistorico() {
      this.navegacaoDoHistorico = estadoInicialDaNavegacao()
    },
    falaDoEnvioParaHistorico() {
      const texto = String(this.value || '')
      return texto.trim() ? this.comComandoDoModulo(texto) : texto
    },
    registrarNoHistorico(texto) {
      this.reiniciarNavegacaoDoHistorico()
      if (!this.historicoDePrompts) return
      registrarPrompt(texto, { escopo: this.escopoDoHistorico })
    },
  },
}
